import {AfterContentChecked, Component, ElementRef, HostBinding, Input, ViewChild} from '@angular/core';
import {MatTooltip} from '@angular/material/tooltip';

export enum BadgeColors {
    primary = 'brand-primary',
    secondary = 'brand-secondary',
    tertiary = 'brand-tertiary',
    neutral = 'neutral-white',
    success = 'feedback-success',
    warning = 'feedback-warning',
    danger = 'feedback-error',
    white = 'support-white',
    green = 'support-green',
    lightgreen = 'light-green',
    yellow = 'yellow',
    info = 'feedback-info',
}

type ColorInput = (keyof typeof BadgeColors) | BadgeColors;

@Component({
    selector: 'ui-badge',
    templateUrl: './ui-badge.component.html',
    styleUrls: ['./ui-badge.component.scss'],
    standalone: false,
    hostDirectives: [
        {
            directive: MatTooltip,
            inputs: ['matTooltip: tooltip', 'matTooltipPosition: tooltipPosition']
        }
    ]
})
export class UiBadgeComponent implements AfterContentChecked {
    @Input() variant: 'fill' | 'outline' = 'fill';
    @Input() size: 'small' | 'medium' | 'large' = 'medium';
    @Input() tooltip: string;
    @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' | 'before' | 'after' = 'below';

    @ViewChild('text') text!: ElementRef<HTMLSpanElement>;

    private _color: ColorInput = 'primary';
    private _lastTooltipText: string;

    constructor(private matTooltip: MatTooltip) {
    }

    ngAfterContentChecked(): void {
        if (this.tooltip) {
            return; // Tooltip externo tem prioridade
        }

        const currentText = this.text?.nativeElement?.textContent?.trim() ?? '';
        if (currentText && currentText !== this._lastTooltipText) {
            this._lastTooltipText = currentText;
            this.matTooltip.message = currentText;
        }
    }

    @Input()
    get color(): ColorInput {
        return this._color;
    }

    set color(value: (keyof typeof BadgeColors) | BadgeColors | 'auto' | string) {
        if (value === this._color) {
            return;
        }

        if (value === 'auto') {
            this.updateColor();
            return;
        }

        // 1. se for uma key válida de BadgeColors
        if (Object.prototype.hasOwnProperty.call(BadgeColors, value)) {
            this._color = value as keyof typeof BadgeColors;
            return;
        }

        // 2. se for um value de BadgeColors, buscar a key correspondente
        const entry = Object.entries(BadgeColors)
            .find(([, v]) => v === value);

        if (entry) {
            const [key] = entry;
            this._color = key as keyof typeof BadgeColors;
            return;
        }

        // 3. se não for nem key nem value
        this._color = 'primary'; // valor padrão interno para auto (ou outro de sua escolha)
    }


    @HostBinding('class') get getBadgeClasses() {
        const classes = [
            `ui-badge__${this.variant}`,
            this._color,
            `ui-badge__${this.size}`
        ];
        return classes.filter(Boolean).join(' ');
    }

    private updateColor(): void {
        const colorKeys = Object.keys(BadgeColors)
            .filter(k => isNaN(Number(k))) as (keyof typeof BadgeColors)[];

        if (!colorKeys.length) {
            this._color = 'primary';
            return;
        }

        const randomIndex = Math.floor(Math.random() * colorKeys.length);

        this._color = colorKeys[randomIndex];
    }
}
